import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import type { AlarmException } from "@src/services/api/types";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import { isExceptionAllDayRange } from "@src/features/schedules/utils/schedule-utils";
import { prunePastExceptions, type ExceptionDisplayItem } from "@src/features/alarms/utils/alarm-utils";
import { Icon, type IconNameType } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import { FC, memo } from "react";

interface ExceptionsPreviewProps {
  exceptions: AlarmException[];
  /** Optional diff display items for agent recommendations (add/remove styling). When provided, used instead of exceptions. */
  displayItems?: ExceptionDisplayItem[];
  onExceptionPress?: (exceptionId: string) => void;
  accessoryIcon?: IconNameType;
  timezone?: string;
}

const toLocalDate = (record: AlarmException["period"]["start"] | null | undefined, timezone?: string): Date | null => {
  if (!record) {
    return null;
  }

  return timezone ?
      dateTimeRecordToLocalisedDateTime(record, timezone).toJSDate()
    : new Date(record.year, record.month - 1, record.day, record.hour ?? 0, record.minute ?? 0);
};

function formatExceptionDate(exception: AlarmException, timezone?: string): string {
  if (!exception.period?.start) {
    return "Invalid date";
  }
  const startDate = toLocalDate(exception.period.start, timezone);
  if (!startDate) {
    return "Invalid date";
  }
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(timezone && { timeZone: timezone }),
  }).format(startDate);
}

function isExceptionAllDay(exception: AlarmException): boolean {
  if (!exception.period?.start || !exception.period?.end) {
    return false;
  }
  return isExceptionAllDayRange(exception.period.start, exception.period.end);
}

function formatExceptionActionSentence(exception: AlarmException, timezone?: string): string {
  const action = exception.action === "armed" ? "Armed" : "Disarmed";

  if (!exception.period?.start || !exception.period?.end) {
    return `(Invalid time)`;
  }

  if (isExceptionAllDay(exception)) {
    return `all day`;
  }

  const startDate = toLocalDate(exception.period.start, timezone);
  const endDate = toLocalDate(exception.period.end, timezone);
  if (!startDate || !endDate) {
    return `${action} (Invalid time)`;
  }

  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...(timezone && { timeZone: timezone }),
  });

  return `${timeFormatter.format(startDate)} - ${timeFormatter.format(endDate)}`;
}

const filterDisplayItems = (items: ExceptionDisplayItem[], now: Date, tz: string): ExceptionDisplayItem[] =>
  items.filter((i) => i.type === "removed" || prunePastExceptions([i.exception], now, tz).length > 0);

const RemovedCardStyle = (theme: { colors: { exceptionPreviewCards: { removed: { border: string } } } }) => ({
  borderColor: theme.colors.exceptionPreviewCards.removed.border,
});

const ModifiedCardStyle = (theme: { colors: { exceptionPreviewCards: { modified: { border: string } } } }) => ({
  borderColor: theme.colors.exceptionPreviewCards.modified.border,
});

export const ExceptionsPreview: FC<ExceptionsPreviewProps> = memo(
  ({ exceptions, displayItems, onExceptionPress, accessoryIcon, timezone }) => {
    const { theme } = useUnistyles();
    const tz = timezone ?? "UTC";
    const now = new Date();
    const itemsToRender =
      displayItems ?
        filterDisplayItems(displayItems, now, tz)
      : prunePastExceptions(
          exceptions.filter((e) => e.period?.start && e.period?.end),
          now,
          tz,
        ).map((exception) => ({
          type: "kept" as const,
          exception,
        }));

    if (itemsToRender.length === 0) {
      if (displayItems?.length) return null;
      return (
        <Card py={16}>
          <View alignItems="center">
            <Icon
              name="calendarOutline"
              size={48}
              color={theme.colors.textMuted}
              style={{ marginBottom: theme.spacing["2xl"] }}
            />
            <Text textStyle="md/semibold" fontWeight="600" mb="xs" textAlign="center">
              No upcoming exceptions
            </Text>
            <Text color="textMuted" textStyle="sm/regular" textAlign="center" px="xl">
              Exceptions allow you to make temporary changes to your routine, such as arming the site all day on key
              dates.
            </Text>
          </View>
        </Card>
      );
    }

    return (
      <View gap="3xl">
        {itemsToRender.map((item) => {
          const { exception } = item;
          const removed = item.type === "removed";
          const modified = item.type === "modified";
          const mutedColor = theme.colors.textMuted as string;
          return (
            <PressableScale key={exception.id} onPress={() => !removed && onExceptionPress?.(exception.id)}>
              <Card
                style={
                  removed ? RemovedCardStyle(theme)
                  : modified ?
                    ModifiedCardStyle(theme)
                  : undefined
                }
              >
                <View flexDirection="row" justifyContent="space-between" alignItems="center" gap="md">
                  <View flex={1}>
                    <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                      <View style={{ overflow: "hidden" }}>
                        <DiagonalStripeSquare state={exception.action} size={20} />
                      </View>
                      <Text textStyle="sm/medium" numberOfLines={2} ellipsizeMode="tail">
                        {exception.reason}
                      </Text>
                      {removed ?
                        <AlarmStateBadge state="removed" />
                      : modified ?
                        <AlarmStateBadge state="modified" />
                      : <AlarmStateBadge state={exception.action} />}
                    </View>
                    <View flexDirection="row" alignItems="center" gap="xs">
                      <Icon name="calendarOutline" color={mutedColor} size={16} />
                      <Text textStyle="xs/regular" numberOfLines={1} color="textMuted">
                        {formatExceptionDate(exception, timezone)}
                      </Text>
                      <Text textStyle="xs/regular" color="textMuted">
                        •
                      </Text>
                      <Icon name="clockOutline" color={mutedColor} size={16} />
                      <Text textStyle="xs/regular" color="textMuted">
                        {toSentenceCase(formatExceptionActionSentence(exception, timezone))}
                      </Text>
                    </View>
                  </View>
                  {!removed && onExceptionPress && accessoryIcon && (
                    <Icon name={accessoryIcon} color={theme.colors.icon} />
                  )}
                </View>
              </Card>
            </PressableScale>
          );
        })}
      </View>
    );
  },
);
